<script setup lang="ts">
import type { Product } from '@/services/product'

import { formatMoneyWithCurrency } from '@/lib/formatters/money'
import { extractShopifyId } from '@/lib/gid'
import { getOriginalPrice, getPrice } from '@/lib/sellingPlans'
import { track } from '@/lib/track'
import { AsyncOperationState } from '@/stores/lib/AsyncOperationState'

import IconArrowRight from '@/assets/icons/arrow-right.svg?component'
import Button from '@/components/Button.vue'
import ProductImage from '@/components/ProductImage.vue'

const props = defineProps<{
  title: string
  products: Product[]
  loadState: AsyncOperationState
  section?: string
}>()

const onProductClick = (product: Product) => {
  if (!props.section) return
  track('recommendation_product_clicked', {
    section: props.section,
    product_id: extractShopifyId(product.id),
    product_title: product.title,
  })
}
</script>

<template>
  <div class="HorizontalProductList">
    <div class="HorizontalProductList__Headings">
      <h2 class="HorizontalProductList__Title">{{ title }}</h2>
      <Button :link="true" href="/shop">
        View all products
        <template #iconAfter><IconArrowRight width="16" /></template>
      </Button>
    </div>
    <div
      v-if="loadState === AsyncOperationState.DONE"
      class="HorizontalProductList__Items"
    >
      <div
        v-for="product in products"
        :key="product.id"
        class="HorizontalProductList__Item"
      >
        <a
          :href="`/product/${extractShopifyId(product.id)}?target=browse`"
          @click="onProductClick(product)"
        >
          <ProductImage
            class="HorizontalProductList__Image"
            :alt="product.title"
            :url="product.images.edges[0]?.node?.small"
          ></ProductImage>
        </a>
        <div class="HorizontalProductList__ItemContent">
          <p class="HorizontalProductList__ItemTitle">
            <a
              :href="`/product/${extractShopifyId(product.id)}?target=browse`"
              @click="onProductClick(product)"
            >
              {{ product.title }}
            </a>
          </p>
          <div class="HorizontalProductList__ItemPriceContainer">
            <span class="HorizontalProductList__ItemPriceDiscounted">
              {{
                formatMoneyWithCurrency(
                  getPrice(
                    product,
                    product.variants.edges[0].node.id,
                    product.sellingPlanGroups.edges[0]?.node.sellingPlans
                      .edges[0]?.node.id ?? null,
                  ) / 100,
                )
              }}</span
            >
            <span
              v-if="
                getOriginalPrice(product, product.variants.edges[0].node.id) >
                getPrice(
                  product,
                  product.variants.edges[0].node.id,
                  product.sellingPlanGroups.edges[0]?.node.sellingPlans.edges[0]
                    ?.node.id ?? null,
                )
              "
              class="HorizontalProductList__ItemPriceOriginal"
            >
              {{
                formatMoneyWithCurrency(
                  getOriginalPrice(product, product.variants.edges[0].node.id) /
                    100,
                )
              }}</span
            >
          </div>
        </div>
        <Button
          :modifiers="['block']"
          :href="`/product/${extractShopifyId(product.id)}?target=browse`"
          @click="onProductClick(product)"
          >View details</Button
        >
      </div>
    </div>
    <div v-else class="HorizontalProductList__Items">
      <div v-for="i in 4" :key="i" class="HorizontalProductList__Item">
        <ProductImage
          class="HorizontalProductList__Image"
          alt="Loading..."
        ></ProductImage>
        <div class="HorizontalProductList__ItemContent">
          <p class="HorizontalProductList__ItemTitle GhostUI">Title</p>
          <div class="HorizontalProductList__ItemPriceContainer GhostUI">
            <span class="HorizontalProductList__ItemPriceDiscounted GhostUI">
              10.00
            </span>
            <span class="HorizontalProductList__ItemPriceOriginal GhostUI">
              10.00
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.HorizontalProductList {
  border-radius: 8px;
  border-top: 1px solid var(--color-white);
  background: var(--color-white);
  display: flex;
  padding: 20px 20px 16px;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
}

.HorizontalProductList__Headings {
  margin-bottom: 10px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  align-items: flex-start;

  @media (--mq-tablet-and-up) {
    flex-direction: row;
    align-items: end;
    justify-content: space-between;
  }
}

.HorizontalProductList__ItemContent {
  margin: 8px 0;
  flex-grow: 1;
}

.HorizontalProductList__Title {
  margin: 0 0 8px;
  color: var(--color-forest-100);
  font-family: var(--font-family-domaine);
  font-size: 26px;
  font-style: normal;
  font-weight: 400;
  line-height: 26px;

  @media (--mq-tablet-and-up) {
    margin: 0;
  }
}

.HorizontalProductList__Image {
  height: 164px;
  width: 164px;
  border-radius: 4px;
}

.HorizontalProductList__Items {
  width: 100%;
  display: flex;
  gap: 24px;
  overflow-x: auto;
  padding-bottom: 16px;
}

.HorizontalProductList__Item {
  width: 164px;
  display: flex;
  flex-direction: column;
}

.HorizontalProductList__ItemTitle {
  margin: 0 0 8px;
  color: var(--color-forest-100);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px; /* 150% */
  & > a {
    color: var(--color-forest-100);
  }
}

.HorizontalProductList__ItemPriceContainer {
  display: flex;
  gap: 4px;
  align-items: end;
  font-weight: 700;
  line-height: 20px; /* 150% */
}

.HorizontalProductList__ItemPriceDiscounted {
  color: var(--color-forest-100);
  font-size: 16px;
  font-style: normal;
}

.HomeView__MessageCta {
  border-radius: 8px;
  color: var(--color-forest-100);
  background: var(--Lichen-100, #e4e8e7);

  svg {
    color: var(--color-forest-100);
  }
}

.HorizontalProductList__ItemPriceOriginal {
  color: var(--color-forest-60);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  text-decoration-line: line-through;
}
</style>
